<template>
	<SideNavLayout>
		<div class="hunt-page">
			<div class="page-header">
				<h1>{{ $t("nav.hunt") }}</h1>
				<p class="subtitle">{{ $t("hunt-page.subtitle") }}</p>
			</div>

			<div v-if="store.CurrentUserDetails" class="projects-table-section">
				<div class="toolbar">
					<TableSearch v-model="searchQuery" :placeholder="$t('hunt-page.search-placeholder')" />
					<Button
						v-if="canManageProjects"
						variant="primary"
						shape="pill"
						:size="isMobile ? 's' : 'm'"
						@click="openCreateModal"
					>
						+ {{ $t("hunt-page.new-project") }}
					</Button>
				</div>

				<div v-if="sortedProjects.length > 0" class="table-wrapper">
					<div v-if="isMobile" class="cards-scroll">
						<div
							v-for="project in sortedProjects"
							:key="project.id"
							class="project-card"
							@click="navigateToProject(project.id)"
						>
							<div class="card-header">
								<span class="client-name">{{ displayName(project) }}</span>
								<button
									v-if="canEditProject(project)"
									class="edit-btn"
									type="button"
									:aria-label="$t('hunt-page.edit-project-aria')"
									@click.stop="openEditModal(project)"
								>
									<AppIcon :name="IconName.Pencil" :size="14" :stroke-width="2" />
								</button>
							</div>
							<div class="card-meta">
								<span>{{ $t("hunt-page.columns.expiry") }}: {{ formatExpiry(project.expiryDate) }}</span>
								<span class="meta-dot" aria-hidden="true">·</span>
								<span>{{ project.targetsCount }} {{ targetsLabel }}</span>
								<span class="meta-dot" aria-hidden="true">·</span>
								<span class="meta-activity">{{ formatActivity(project.updatedAt) }}</span>
							</div>
							<div v-if="project.brokers.length > 0 || project.prospectors.length > 0" class="card-people">
								<span v-if="project.brokers.length > 0">
									{{ $t("hunt-page.columns.brokers") }}: {{ formatPeople(project.brokers) }}
								</span>
								<span v-if="project.prospectors.length > 0">
									{{ $t("hunt-page.columns.prospector") }}: {{ formatPeople(project.prospectors) }}
								</span>
							</div>
						</div>
					</div>
					<div v-else class="table-scroll">
						<table>
							<thead>
								<tr>
									<th class="col-client">{{ $t("hunt-page.columns.client") }}</th>
									<th class="sortable" :data-sort-order="sortOrderFor('expiryDate')" @click="toggleSort('expiryDate')">
										{{ $t("hunt-page.columns.expiry") }}
									</th>
									<th>{{ $t("hunt-page.columns.brokers") }}</th>
									<th>{{ $t("hunt-page.columns.prospector") }}</th>
									<th
										class="col-numeric sortable"
										:data-sort-order="sortOrderFor('targetsCount')"
										@click="toggleSort('targetsCount')"
									>
										{{ $t("hunt-page.columns.targets") }}
									</th>
									<th
										class="col-activity sortable"
										:data-sort-order="sortOrderFor('updatedAt')"
										@click="toggleSort('updatedAt')"
									>
										{{ $t("hunt-page.columns.activity") }}
									</th>
									<th class="col-actions" aria-hidden="true"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="project in sortedProjects" :key="project.id" @click="navigateToProject(project.id)">
									<td class="col-client">
										<span class="client-name">{{ displayName(project) }}</span>
									</td>
									<td>{{ formatExpiry(project.expiryDate) }}</td>
									<td>{{ formatPeople(project.brokers) }}</td>
									<td>{{ formatPeople(project.prospectors) }}</td>
									<td class="col-numeric">{{ project.targetsCount }}</td>
									<td class="col-activity">{{ formatActivity(project.updatedAt) }}</td>
									<td class="col-actions">
										<button
											v-if="canEditProject(project)"
											class="edit-btn"
											type="button"
											:aria-label="$t('hunt-page.edit-project-aria')"
											@click.stop="openEditModal(project)"
										>
											<AppIcon :name="IconName.Pencil" :size="14" :stroke-width="2" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<p v-else class="empty-state">{{ $t("hunt-page.empty-state") }}</p>
			</div>
		</div>

		<ProjectModal
			:is-open="isProjectModalOpen"
			:current-project="projectToEdit"
			@close="closeProjectModal"
			@created="handleProjectCreated"
		/>
	</SideNavLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useMediaQuery } from "@vueuse/core"
import SideNavLayout from "@/components/layout/SideNavLayout.vue"
import Button from "@/components/ui/Button.vue"
import TableSearch from "@/components/table/TableSearch.vue"
import ProjectModal from "@/components/modals/ProjectModal.vue"
import { ProjectType } from "@/graphql"
import { Permission, UseRootStore } from "@/model/RootStore"
import { getRelativeTimeParts } from "@/utils/date"
import { projectDisplayName } from "@/utils/project"
import type { CurrentUserProject, CurrentUserProjectPerson } from "@/utils/ApiClient"
import type { Project } from "@/model/DataModel"

const store = UseRootStore()
const router = useRouter()
const { userProjects } = storeToRefs(store)
const { t, locale } = useI18n()

type SortKey = "updatedAt" | "expiryDate" | "targetsCount"

const isProjectModalOpen = ref(false)
const projectToEdit = ref<CurrentUserProject | null>(null)
const searchQuery = ref("")
const sortKey = ref<SortKey>("updatedAt")
const sortDescending = ref(true)

const canManageProjects = computed(() => store.currentUser?.has(Permission.ProjectWrite))
const isMobile = useMediaQuery("(max-width: 768px)")
const targetsLabel = computed(() => t("hunt-page.columns.targets").toLowerCase())

onMounted(() => {
	if (!store.CurrentUserDetails || !store.currentUserDetailsIncludesProjectPeople) {
		store.fetchCurrentUserDetails()
	}
})

function displayName(project: CurrentUserProject): string {
	return projectDisplayName(project, t)
}

function canEditProject(project: CurrentUserProject): boolean {
	return canManageProjects.value === true && project.type === "CCA"
}

function compareNullable(a: number | null, b: number | null, direction: 1 | -1): number {
	if (a === null && b === null) return 0
	if (a === null) return 1
	if (b === null) return -1
	return (a - b) * direction
}

function compareProjects(a: CurrentUserProject, b: CurrentUserProject): number {
	const direction = sortDescending.value ? -1 : 1

	if (sortKey.value === "expiryDate") {
		const aTime = a.expiryDate ? new Date(a.expiryDate).getTime() : null
		const bTime = b.expiryDate ? new Date(b.expiryDate).getTime() : null
		return compareNullable(aTime, bTime, direction)
	}

	if (sortKey.value === "targetsCount") {
		return compareNullable(a.targetsCount ?? null, b.targetsCount ?? null, direction)
	}

	return (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()) * direction
}

const sortedProjects = computed(() => {
	const query = searchQuery.value.trim().toLowerCase()
	const filtered = query
		? userProjects.value.filter((project: CurrentUserProject) => displayName(project).toLowerCase().includes(query))
		: userProjects.value.slice()

	return filtered.sort((a: CurrentUserProject, b: CurrentUserProject) => {
		const rank = (project: CurrentUserProject) => (project.type === ProjectType.Workspace ? 0 : 1)
		return rank(a) - rank(b) || compareProjects(a, b)
	})
})

function toggleSort(key: SortKey): void {
	if (sortKey.value === key) {
		sortDescending.value = !sortDescending.value
	} else {
		sortKey.value = key
		sortDescending.value = true
	}
}

function sortOrderFor(key: SortKey): "asc" | "desc" | undefined {
	return sortKey.value === key ? (sortDescending.value ? "desc" : "asc") : undefined
}

function formatPeople(people: CurrentUserProjectPerson[]): string {
	const names = people.map(person => person.name).filter((name): name is string => !!name)
	return names.length > 0 ? names.join(", ") : t("hunt-page.no-brokers")
}

function formatExpiry(expiryDate?: string | null): string {
	if (!expiryDate) return t("hunt-page.no-expiry")
	return new Intl.DateTimeFormat(locale.value === "en" ? "en-CA" : "fr-CA", {
		day: "numeric",
		month: "short",
		year: "numeric",
	}).format(new Date(expiryDate))
}

function formatActivity(updatedAt: string): string {
	const date = new Date(updatedAt)
	const parts = getRelativeTimeParts(date)
	if (parts.key === "absolute") return formatExpiry(updatedAt)
	if ("count" in parts) return t(`hunt-page.relative-time.${parts.key}`, { count: parts.count })
	return t(`hunt-page.relative-time.${parts.key}`)
}

function navigateToProject(projectId: string): void {
	router.push({ path: "/", query: { project: projectId } })
}

function openCreateModal(): void {
	projectToEdit.value = null
	isProjectModalOpen.value = true
}

function openEditModal(project: CurrentUserProject): void {
	projectToEdit.value = project
	isProjectModalOpen.value = true
}

function handleProjectCreated(project: Project): void {
	projectToEdit.value = {
		id: project.id,
		name: project.name,
		createdAt: project.createdAt,
		updatedAt: project.updatedAt,
		expiryDate: project.expiryDate,
		ccaContractHubspotId: project.ccaContractHubspotId,
		brokers: project.brokers,
		prospectors: project.prospectors,
		targetsCount: project.targetsCount,
	}
}

function closeProjectModal(): void {
	isProjectModalOpen.value = false
	projectToEdit.value = null
	store.fetchCurrentUserDetails()
}
</script>

<style scoped>
.hunt-page {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);
	padding: var(--spacing-xl);
	height: 100%;
	min-height: 0;
	font-family: var(--font-family-primary);
}

.page-header {
	h1 {
		margin: 0;
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--neutral-900);
	}

	.subtitle {
		margin: var(--spacing-xs) 0 0;
		font-size: var(--font-size-small);
		color: var(--neutral-600);
	}
}

.projects-table-section {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);
	flex: 1;
	min-height: 0;
}

.toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--spacing-sm);

	:deep(.input) {
		height: var(--size-button-s);
	}
}

.table-wrapper {
	display: flex;
	flex-direction: column;
	flex: 0 1 auto;
	min-height: 0;
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	overflow: hidden;
	background: var(--neutral-0);
}

.table-scroll {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	overflow-x: auto;
}

table {
	width: 100%;
	border-collapse: collapse;

	thead {
		position: sticky;
		top: 0;
		z-index: 1;
		background: var(--neutral-50);

		th {
			padding: var(--spacing-sm) var(--spacing-md);
			text-align: left;
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
			letter-spacing: 0.02em;
			text-transform: uppercase;
			color: var(--neutral-600);
			background: var(--neutral-50);
			border-bottom: var(--border-width-thin) solid var(--neutral-200);

			&.sortable {
				cursor: pointer;
				user-select: none;

				&:hover {
					color: var(--neutral-900);
				}

				&::after {
					content: "↕";
					margin-left: var(--space-4);
					opacity: 0.4;
					font-size: var(--font-size-small);
				}

				&[data-sort-order="asc"]::after {
					content: "↑";
					opacity: 0.8;
					color: var(--primary-green);
				}

				&[data-sort-order="desc"]::after {
					content: "↓";
					opacity: 0.8;
					color: var(--primary-green);
				}
			}
		}
	}

	tbody tr {
		cursor: pointer;

		&:not(:last-child) td {
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
		}

		&:hover td {
			background: var(--neutral-50);
		}
	}

	td {
		padding: var(--spacing-sm) var(--spacing-md);
		font-size: var(--font-size-regular);
		color: var(--neutral-900);
		white-space: nowrap;
	}

	td.col-client {
		font-weight: var(--font-weight-medium);
	}

	.col-numeric {
		text-align: right;
		font-weight: var(--font-weight-semibold);
	}

	td.col-activity {
		color: var(--neutral-600);
		font-size: var(--font-size-small);
	}

	.col-actions {
		width: var(--size-button-s);
		text-align: right;
	}
}

.edit-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--size-button-s);
	height: var(--size-button-s);
	border: var(--border-width-thin) solid var(--neutral-400);
	border-radius: var(--border-radius-sm);
	background: var(--neutral-0);
	color: var(--neutral-600);
	cursor: pointer;

	&:hover {
		color: var(--primary-green);
		border-color: var(--primary-green);
	}
}

.empty-state {
	margin: 0;
	padding: var(--spacing-2xl);
	text-align: center;
	color: var(--neutral-600);
}

.cards-scroll {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	padding: var(--spacing-sm);
	background: var(--neutral-50);
	border-radius: var(--border-radius-md);
}

.project-card {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	padding: var(--spacing-md);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-0);
	box-shadow: var(--shadow-sm);
	cursor: pointer;

	&:active {
		background: var(--neutral-50);
	}
}

.card-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: var(--spacing-sm);

	.client-name {
		font-size: var(--font-size-regular);
		font-weight: var(--font-weight-semibold);
		color: var(--neutral-900);
	}

	.edit-btn {
		flex-shrink: 0;
	}
}

.card-meta {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: var(--space-6);
	font-size: var(--font-size-small);
	color: var(--neutral-900);

	.meta-dot {
		color: var(--neutral-400);
	}

	.meta-activity {
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
	}
}

.card-people {
	display: flex;
	flex-direction: column;
	gap: var(--space-4);
	font-size: var(--font-size-xs);
	color: var(--neutral-600);
}

@media (max-width: 768px) {
	.hunt-page {
		padding: var(--spacing-md);
		gap: var(--spacing-md);
	}

	.toolbar {
		flex-wrap: wrap;

		:deep(.input) {
			flex: 1 1 100%;
		}
	}

	.table-wrapper {
		border: none;
		border-radius: 0;
		overflow: visible;
		background: transparent;
	}
}
</style>
